import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface Alert {
    id: string;
    type: 'success' | 'danger' | 'warning' | 'info';
    message: string;
    title?: string;
    autoDismiss?: boolean;
    duration?: number;
}

@Injectable({
    providedIn: 'root'
})
export class AlertService {
    private alertsSubject = new BehaviorSubject<Alert[]>([]);
    alerts$: Observable<Alert[]> = this.alertsSubject.asObservable();

    private generateId(): string {
        return Math.random().toString(36).substring(2, 9);
    }

    private addAlert(alert: Omit<Alert, 'id'>): void {
        const newAlert: Alert = {
            ...alert,
            id: this.generateId(),
            autoDismiss: alert.autoDismiss !== false,
            duration: alert.duration || 5000
        };

        const currentAlerts = this.alertsSubject.value;
        this.alertsSubject.next([...currentAlerts, newAlert]);

        if (newAlert.autoDismiss) {
            setTimeout(() => this.dismiss(newAlert.id), newAlert.duration);
        }
    }

    success(message: string, title?: string, duration?: number): void {
        this.addAlert({ type: 'success', message, title, duration });
    }

    error(message: string, title?: string, duration?: number): void {
        this.addAlert({ type: 'danger', message, title, duration });
    }

    warning(message: string, title?: string, duration?: number): void {
        this.addAlert({ type: 'warning', message, title, duration });
    }

    info(message: string, title?: string, duration?: number): void {
        this.addAlert({ type: 'info', message, title, duration });
    }

    dismiss(id: string): void {
        const currentAlerts = this.alertsSubject.value;
        this.alertsSubject.next(currentAlerts.filter(alert => alert.id !== id));
    }

    dismissAll(): void {
        this.alertsSubject.next([]);
    }
}
